import { useEffect, useState, useCallback, useMemo } from "react";
import { CiSearch } from "react-icons/ci";
import { useSelector } from "react-redux";
import { Empty } from "antd";
import axiosLLInstance from "../../utils/axiosLLInstance";
import BreadcrumbItems from "../../components/BreadcrumbItems";
import debounce from "lodash.debounce";
import Tr from "../common/Tr";
import Td from "../common/Td";
import { notifyUser } from "../../utils/notifyUser";
import { useNavigate, useParams } from "react-router-dom";
import { formatDate } from "../../utils/formatDate";

const columns = ["", "Fleet Name", "Assigned Users", "Created At"];

export default function AssignFleets() {
  const { userId } = useParams();
  const { fleetUser } = useSelector((state) => state.users);
  const [fleets, setFleets] = useState([]);
  const [selectedFleetIds, setSelectedFleetIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const numericUserId = parseInt(userId, 10);

  const fetchFleets = useCallback(async () => {
    if (!fleetUser?.admin_id) return;

    try {
      const { data } = await axiosLLInstance.get(
        `/fleets/admins/${fleetUser.admin_id}/assignable${
          searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ""
        }`
      );

      const updatedFleets = (data || []).map((fleet) => ({
        ...fleet,
        checked: fleet.assigned_users.some((user) => user.id === numericUserId),
      }));

      setFleets(updatedFleets);
      setSelectedFleetIds(
        updatedFleets.filter((f) => f.checked).map((f) => f.id)
      );
    } catch (error) {
      console.error("Error fetching fleets:", error);
      notifyUser({ type: "error", message: "Failed to fetch fleets" });
    }
  }, [fleetUser?.admin_id, numericUserId, searchQuery]);

  const debouncedFetch = useMemo(
    () => debounce(fetchFleets, 500),
    [fetchFleets]
  );

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [searchQuery, debouncedFetch]);

  const handleCheckStatus = (fleetId) => {
    setFleets((prev) =>
      prev.map((fleet) =>
        fleet.id === fleetId ? { ...fleet, checked: !fleet.checked } : fleet
      )
    );

    setSelectedFleetIds((prev) =>
      prev.includes(fleetId)
        ? prev.filter((id) => id !== fleetId)
        : [...prev, fleetId]
    );
  };

  const assignFleets = async () => {
    if (selectedFleetIds.length === 0) {
      notifyUser({ type: "info", message: "Please select at least one fleet" });
      return;
    }

    try {
      await axiosLLInstance.post(`/fleets/assign/${userId}`, selectedFleetIds);
      notifyUser({ type: "success", message: "Fleets assigned successfully" });
      navigate(`/users/${userId}/fleets`);
    } catch (error) {
      console.error("Error assigning fleets:", error);
      notifyUser({ type: "error", message: "Failed to assign fleets" });
    }
  };

  return (
    <div className="flex justify-center mt-8">
      <div className="w-[80%]">
        <div className="ms-5">
          <BreadcrumbItems
            items={[
              { title: "Manage Users", to: "/users" },
              { title: "Fleets", to: `/users/${fleetUser?.id}/fleets` },
              { title: "Assign Fleets" },
            ]}
          />
        </div>

        <div className="flex flex-col items-center my-10">
          <div className="text-[29px] font-medium text-center text-[#1F2937]">
            Assign Fleets to {fleetUser?.name || "User"}
          </div>

          <div className="form-control flex flex-row items-center rounded-[10px] border border-gray-200 px-2 mx-4 my-6 bg-white w-[23rem]">
            <CiSearch className="text-[25px]" />
            <input
              type="text"
              placeholder="Search Fleets"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-4 py-2 rounded w-full text-[16px] focus:outline-none bg-white text-gray-800 placeholder:text-gray-400"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            className="btn bg-slate-950 text-slate-50 text-[16px] font-[500] rounded-xl w-40 hover:bg-slate-950 mb-6 disabled:opacity-50"
            onClick={assignFleets}
          >
            Assign
          </button>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="table w-full">
            <thead className="border-b-2 border-base-300">
              <tr className="text-[#B1B1B1] text-[15px] font-[700] leading-[35px]">
                {columns.map((column) => (
                  <th key={column}>{column}</th>
                ))}
              </tr>
            </thead>

            <tbody className="mt-3">
              {fleets.length > 0 ? (
                fleets.map(
                  ({
                    id,
                    checked,
                    thing_group,
                    assigned_users,
                    created_at,
                  }) => (
                    <Tr key={id}>
                      <td className="bg-white py-4 px-4">
                        <label className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleCheckStatus(id)}
                          />
                        </label>
                      </td>
                      <Td>{thing_group || "-"}</Td>
                      <Td>
                        {assigned_users.length > 0
                          ? assigned_users.map((u) => u.name).join(", ")
                          : "-"}
                      </Td>
                      <Td>{formatDate(created_at) || "-"}</Td>
                    </Tr>
                  )
                )
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-[20px] text-center"
                  >
                    <Empty />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
